import React, { useMemo } from 'react';
import { Button, Grid, Paper, Box, Typography, TextField } from 'components/mui';
import MenuItem from '@mui/material/MenuItem';
import { useAddressFormUnified } from 'containers/pages/addressManagement/hooks';
import * as TY from 'containers/pages/addressManagement/constants';
import AddressTypeSelector from '../addressTypeSelector/AddressTypeSelector';
import LoadingBackdrop from 'components/LoadingBackdrop';
import { PlaceAutoComplete } from 'components';
import { AddressType } from 'containers/pages/addressManagement/types';

interface AddressFormUnifiedProps {
  mode: 'add' | 'edit';
  addressId?: number | null;
  customerId?: number | null;
  addressTypes: AddressType[];
  onSave: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
}

function MissingFieldsHint({ canNotSave, missing }: { canNotSave: boolean; missing: string[] }) {
  if (!canNotSave || missing.length === 0) return null;
  return (
    <Typography color="error" variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
      Please fill in all required fields: {missing.join(', ')}.
    </Typography>
  );
}

export default function AddressFormUnified({
  mode,
  addressId,
  customerId,
  addressTypes,
  onSave,
}: AddressFormUnifiedProps) {
  const { state, handler } = useAddressFormUnified({
    mode,
    addressId,
    customerId,
    onSave,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const address = state.address as any;

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!address[TY.TITLE]) missing.push('Title');
    if (!address[TY.TYPE_ID]) missing.push('Type');
    if (!address[TY.LINE1]) missing.push('Address Line 1');
    if (!address[TY.POSTAL_CODE]) missing.push('Postal Code');
    if (!address[TY.PATROL_TYPE]) missing.push('Patrol Type');
    return missing;
  }, [address]);

  return (
    <Box>
      <Grid container>
        {/* LEFT PAPER — Address Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ borderRadius: 2, padding: 3, margin: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Address Information
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Title"
                name={TY.TITLE}
                value={address[TY.TITLE] || ''}
                onChange={handler.onForm}
                required
                error={state.valid.Title.isErr}
                helperText={state.valid.Title.errMsg}
                fullWidth
                size="small"
              />
              <AddressTypeSelector
                value={address[TY.TYPE_ID] as number | null}
                addressTypes={addressTypes}
                onChange={handler.onTypeChange}
                error={state.valid.TypeId.isErr}
                helperText={state.valid.TypeId.errMsg}
              />
              <PlaceAutoComplete handlePlace={handler.onPlace} />
              <TextField
                label="Address Line 1"
                name={TY.LINE1}
                value={address[TY.LINE1] || ''}
                onChange={handler.onForm}
                required
                error={state.valid.Line1.isErr}
                helperText={state.valid.Line1.errMsg}
                fullWidth
                size="small"
              />
              <TextField
                label="Address Line 2"
                name={TY.LINE2}
                value={address[TY.LINE2] || ''}
                onChange={handler.onForm}
                fullWidth
                size="small"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="City"
                    name={TY.CITY}
                    value={address[TY.CITY] || ''}
                    onChange={handler.onForm}
                    required
                    error={state.valid.City.isErr}
                    helperText={state.valid.City.errMsg}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Province"
                    name={TY.PROVINCE}
                    value={address[TY.PROVINCE] || ''}
                    onChange={handler.onForm}
                    required
                    error={state.valid.Province.isErr}
                    helperText={state.valid.Province.errMsg}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Postal Code"
                    name={TY.POSTAL_CODE}
                    value={address[TY.POSTAL_CODE] || ''}
                    onChange={handler.onForm}
                    required
                    error={state.valid.PostalCode.isErr}
                    helperText={state.valid.PostalCode.errMsg}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country"
                    name={TY.COUNTRY}
                    value={address[TY.COUNTRY] || ''}
                    onChange={handler.onForm}
                    required
                    error={state.valid.Country.isErr}
                    helperText={state.valid.Country.errMsg}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
              <TextField
                label="Online Map URL"
                name={TY.ONLINE_MAP_URL}
                value={address[TY.ONLINE_MAP_URL] || ''}
                onChange={handler.onForm}
                fullWidth
                size="small"
              />
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT PAPER — Patrol Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ borderRadius: 2, padding: 3, margin: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Patrol Information
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                select
                label="Patrol Type"
                name={TY.PATROL_TYPE}
                value={address[TY.PATROL_TYPE] || ''}
                onChange={handler.onForm}
                required
                error={state.valid.PatrolType.isErr}
                helperText={state.valid.PatrolType.errMsg}
                fullWidth
                size="small"
              >
                <MenuItem value="Static">Static</MenuItem>
                <MenuItem value="Mobile">Mobile</MenuItem>
              </TextField>
              <TextField
                label="Instructions"
                name={TY.INSTRUCTIONS}
                value={address[TY.INSTRUCTIONS] || ''}
                onChange={handler.onForm}
                multiline
                minRows={6}
                fullWidth
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Row */}
      <Box display="flex" gap={2} mx={2} mt={1} mb={2} alignItems="flex-start">
        <Box flex="1" />
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <MissingFieldsHint canNotSave={state.canNotSave} missing={missingFields} />
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              disabled={state.canNotSave}
              onClick={handler.onSave}
              title={
                state.canNotSave && missingFields.length > 0
                  ? `Please fill in: ${missingFields.join(', ')}`
                  : undefined
              }
            >
              {mode === 'edit' ? 'SAVE' : 'CREATE'}
            </Button>
            <Button variant="outlined" onClick={handler.onReset}>
              RESET
            </Button>
          </Box>
        </Box>
      </Box>

      <LoadingBackdrop open={state.isLoading} />
    </Box>
  );
}
